import React, { useRef, useState, useEffect } from 'react';
import classNames from 'classnames';
import ReactImageGallery from 'react-image-gallery';

import { propTypes } from '../../../util/types';
import { FormattedMessage, useIntl } from '../../../util/reactIntl';
import {
  AspectRatioWrapper,
  Button,
  IconClose,
  IconArrowHead,
  ResponsiveImage,
} from '../../../components';

import './image-gallery.css';
import css from './ListingImageGallery.module.css';
import IconCollection from '../../../components/IconCollection/IconCollection';

const IMAGE_GALLERY_OPTIONS = {
  showPlayButton: false,
  disableThumbnailScroll: false,
  showThumbnails: true,
};
const MAX_LANDSCAPE_ASPECT_RATIO = 2; // 2:1
const MAX_PORTRAIT_ASPECT_RATIO = 4 / 3;

const getFirstImageAspectRatio = (firstImage, scaledVariant) => {
  if (!firstImage) {
    return { aspectWidth: 1, aspectHeight: 1 };
  }

  const v = firstImage?.attributes?.variants?.[scaledVariant];
  const w = v?.width;
  const h = v?.height;
  const hasDimensions = !!w && !!h;

  return hasDimensions
    ? { aspectWidth: w, aspectHeight: h }
    : { aspectWidth: 1, aspectHeight: 1 };
};

const ListingImageGallery = props => {
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [thumbnailPosition, setThumbnailPosition] = useState('left'); // 👈 dynamic position
  const galleryRef = useRef(null);
  const intl = useIntl();
  const { rootClassName, className, images, imageVariants, thumbnailVariants } = props;

  // 👇 Responsive thumbnail behavior
  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth < 992) {
        setThumbnailPosition('bottom');
      } else {
        setThumbnailPosition('left');
      }
    };

    handleResize(); // run once on mount
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const thumbVariants = thumbnailVariants || imageVariants;
  const { aspectWidth, aspectHeight } = getFirstImageAspectRatio(images?.[0], imageVariants[0]);

  const items = images.map((img, i) => {
    const variants = img.attributes?.variants || {};
    const thumbnailUrl = variants[thumbVariants[0]]?.url || variants['scaled-small']?.url || '';
    let originalUrl = '';
    for (const variantName of imageVariants) {
      if (variants[variantName]?.url) {
        originalUrl = variants[variantName].url;
        break;
      }
    }

    return {
      original: originalUrl,
      alt: intl.formatMessage(
        { id: 'ListingImageGallery.imageAltText' },
        { index: i + 1, count: images.length }
      ),
      thumbAlt: intl.formatMessage(
        { id: 'ListingImageGallery.imageThumbnailAltText' },
        { index: i + 1, count: images.length }
      ),
      thumbnail: thumbnailUrl,
      image: img,
    };
  });

  const imageSizesMaybe = isFullscreen
    ? {}
    : { sizes: `(max-width: 1024px) 100vw, (max-width: 1200px) calc(100vw - 192px), 708px` };

  const renderItem = item => (
    <AspectRatioWrapper
      width={aspectWidth || 1}
      height={aspectHeight || 1}
      className={isFullscreen ? css.itemWrapperFullscreen : css.itemWrapper}
    >
      <div className={css.itemCentering}>
        <ResponsiveImage
          rootClassName={css.item}
          image={item.image}
          alt={item.alt}
          variants={imageVariants}
          {...imageSizesMaybe}
        />
      </div>
    </AspectRatioWrapper>
  );

  const renderThumbInner = item => (
    <div className={css.thumbWrapper}>
      <img className={css.thumb} src={item.thumbnail} alt={item.thumbAlt} />
    </div>
  );

  const onScreenChange = isFull => setIsFullscreen(isFull);

  const renderLeftNav = (onClick, disabled) => (
    <button className={css.navLeft} disabled={disabled} onClick={onClick}>
      <div className={css.navArrowWrapper}>
        <IconArrowHead direction="left" size="big" />
      </div>
    </button>
  );

  const renderRightNav = (onClick, disabled) => (
    <button className={css.navRight} disabled={disabled} onClick={onClick}>
      <div className={css.navArrowWrapper}>
        <IconArrowHead direction="right" size="big" />
      </div>
    </button>
  );

  const renderFullscreenButton = (onClick, isFullscreen) =>
    isFullscreen ? (
      <Button
        onClick={onClick}
        rootClassName={css.close}
        title={intl.formatMessage({ id: 'ListingImageGallery.closeModalTitle' })}
      >
        <span className={css.closeText}>
          <FormattedMessage id="ListingImageGallery.closeModal" />
        </span>
        <IconClose rootClassName={css.closeIcon} />
      </Button>
    ) : (
      <Button className={classNames(css.openFullscreen, css.showAllBtn)} onClick={onClick}>
        <IconCollection name={'6dotGroup'} />
        <FormattedMessage id="ListingImageGallery.openFullscreen" />
      </Button>
    );

  const classes = classNames(rootClassName || css.root, className);

  return (
    <ReactImageGallery
      ref={galleryRef}
      additionalClass={classes}
      items={items}
      renderItem={renderItem}
      renderThumbInner={renderThumbInner}
      renderLeftNav={renderLeftNav}
      renderRightNav={renderRightNav}
      onScreenChange={onScreenChange}
      renderFullscreenButton={renderFullscreenButton}
      showThumbnails={true}
      thumbnailPosition={thumbnailPosition} // 👈 dynamic
      {...IMAGE_GALLERY_OPTIONS}
    />
  );
};

export default ListingImageGallery;
